import React, {useContext, useEffect, useState} from "react";
import "./Details.css";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {FormattedMessage, useIntl} from "react-intl";
import {
  GroupDetailResponse,
  GroupLocationState,
  GroupMemberRequest,
  GroupMemberResponse,
} from "../../../logic/APITypes";
import API, {Resolution} from "../../../logic/API";
import {KeycloakContext} from "../../contextProviders/KeycloakProvider";
import {LoadingSkeleton} from "@spg/component_library";
import chevron from "../../../resources/icons/chevron-left.svg";
import edit from "../../../resources/icons/edit.svg";
import user from "../../../resources/icons/user.svg";
import trash from "../../../resources/icons/trash.svg";
import leave from "../../../resources/icons/leave.svg";
import GroupCreation from "./Creation";
import {NameLogo} from "../../../components/NameLogo";
import {ModalContext} from "../../contextProviders/ModalProvider";
import GroupMemberList from "./MemberList";
import {SnackbarContext} from "../../../utility/SnackbarProvider";
import GroupMailInputs from "./MailInputs";
import DetailsApps from "./DetailsApps";

/** A tile element of the apps list containing overview information of installed applications */
function GroupDetails() {
  const {formatMessage} = useIntl();
  const navigate = useNavigate();
  const {state}: {state: null | GroupLocationState} = useLocation();
  const {isSchAdmin, keycloak} = useContext(KeycloakContext);
  const modalContext = useContext(ModalContext);
  const {addSnackbar} = useContext(SnackbarContext);
  let {groupId} = useParams();
  groupId = groupId || "";
  const [group, setGroup] = useState<Resolution<GroupDetailResponse>>({
    unresolved: true,
    success: false,
  });
  const [members, setMembers] = useState<Resolution<GroupMemberResponse[]>>({
    unresolved: true,
    success: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [invitations, setInvitations] = useState<GroupMemberRequest[]>([]);
  const [waitingForInvitations, setWaitingForInvitations] = useState(false);
  const usableInvitations = invitations
    .map((invitation) => ({
      ...invitation,
      email: invitation.email.trim(),
    }))
    .filter((invitation) => invitation.email.length > 0);
  const isGroupAdmin = isSchAdmin || !!group.payload?.is_admin;

  useEffect(() => {
    API.getGroup(groupId).then((response) => {
      setGroup(response);
    });
    if (isSchAdmin)
      API.getGroupMembers(groupId).then((response) => setMembers(response));
  }, []);

  useEffect(() => {
    if (!isSchAdmin && group.payload?.is_admin)
      API.getGroupMembers(groupId).then((response) => setMembers(response));
  }, [group]);

  if (isEditing && group.payload)
    return (
      <GroupCreation
        editing={{
          prefill: {...group.payload, groupId},
          updateGroup: (name: string, description: string) => {
            setGroup((old) =>
              !old.payload
                ? old
                : {...old, payload: {...old.payload, name, description}},
            );
            setIsEditing(false);
          },
        }}
      />
    );

  return (
    <div className="groupDetails">
      <div className="groupDetails__headerBlock">
        <div>
          <h1 className="groupDetails__header">
            <Link to={state?.from === "home" ? "/" : "/groups"}>
              <img
                src={chevron}
                alt={formatMessage({
                  id:
                    "groupDetails.img.alt.chevron." +
                    (state?.from === "home" ? "home" : "groups"),
                })}
              />
            </Link>
            {group.payload?.name || (
              <FormattedMessage id="groupDetails.header.page" />
            )}
            {group.payload && isGroupAdmin && (
              <>
                <button
                  className="iconButton"
                  onClick={() => setIsEditing(true)}
                >
                  <img
                    src={edit}
                    alt={formatMessage({id: "groupDetails.img.alt.edit"})}
                  />
                </button>
                <button
                  className="iconButton"
                  onClick={() =>
                    modalContext.showModal({
                      headline: formatMessage({
                        id: "groupDetails.modal.delete.headline",
                      }),
                      body: formatMessage({
                        id: "groupDetails.modal.delete.body",
                      }),
                      primaryButton: {
                        name: formatMessage({
                          id: "groupDetails.modal.delete.confirm",
                        }),
                        onClick: () =>
                          API.deleteGroup(groupId).then((resonse) => {
                            if (resonse.success) navigate("/groups");
                            else
                              addSnackbar({
                                variant: "close",
                                text: formatMessage({
                                  id: "groupDetails.snackbar.delete.error",
                                }),
                                hasCloseButton: true,
                              });
                            modalContext.hideModal();
                          }),
                      },
                      secondaryButton: {
                        name: formatMessage({
                          id: "groupDetails.modal.delete.cancel",
                        }),
                        onClick: modalContext.hideModal,
                      },
                    })
                  }
                >
                  <img
                    src={trash}
                    alt={formatMessage({id: "groupDetails.img.alt.delete"})}
                  />
                </button>
              </>
            )}
            {group.payload &&
              (!isGroupAdmin ||
                (members.payload &&
                  members.payload.some(
                    (member) => member.id === keycloak.subject,
                  ))) && (
                <button
                  className="iconButton"
                  onClick={() =>
                    modalContext.showModal({
                      headline: formatMessage({
                        id: "groupDetails.modal.leave.headline",
                      }),
                      body: formatMessage({
                        id: "groupDetails.modal.leave.body",
                      }),
                      primaryButton: {
                        name: formatMessage({
                          id: "groupDetails.modal.leave.confirm",
                        }),
                        onClick: () =>
                          API.removeGroupMember(
                            groupId,
                            keycloak.subject || "",
                          ).then((response) => {
                            if (response.success) navigate("/groups");
                            else
                              addSnackbar({
                                variant: "close",
                                text: formatMessage({
                                  id: "groupDetails.snackbar.leave.error",
                                }),
                                hasCloseButton: true,
                              });
                            modalContext.hideModal();
                          }),
                      },
                      secondaryButton: {
                        name: formatMessage({
                          id: "groupDetails.modal.leave.cancel",
                        }),
                        onClick: modalContext.hideModal,
                      },
                    })
                  }
                >
                  <img
                    src={leave}
                    alt={formatMessage({id: "groupDetails.img.alt.leave"})}
                  />
                </button>
              )}
          </h1>
          {group.payload && (
            <div className="groupDetails__description">
              {group.payload.description}
            </div>
          )}
        </div>
        {group.payload && (
          <div className="groupDetails__headerLogo">
            <NameLogo text={group.payload.name} id={groupId} size={128} />
          </div>
        )}
      </div>
      <div className="groupDetails__body">
        {group.unresolved ? (
          <div className="groupDetails__skeleton">
            <LoadingSkeleton />
          </div>
        ) : !group.payload ? (
          <p className="groupDetails__noItems">
            <FormattedMessage id="groupDetails.paragraph.error" />
          </p>
        ) : (
          <>
            <DetailsApps
              group={group}
              isGroupAdmin={isGroupAdmin}
              groupId={groupId}
            />
            <h2>
              <FormattedMessage id="groupDetails.header.admins" />
            </h2>
            <div className="groupDetails__list">
              {group.payload.admins.map((admin) => (
                <Link
                  className="groupDetails__listItem"
                  key={admin.email}
                  to={"mailto:" + admin.email}
                >
                  <NameLogo
                    text={admin.first_name[0] + admin.last_name[0]}
                    id={
                      "3-" +
                      admin.email // turn first three characters into hex to randomize the logo
                        .substring(0, 4)
                        .split("")
                        .map((c) =>
                          c.charCodeAt(0).toString(16).padStart(2, "0"),
                        )
                        .join("-")
                    }
                    size={48}
                  />
                  {admin.first_name + " " + admin.last_name}
                </Link>
              ))}
              <div className="groupDetails__listItem">
                <span className="groupDetails__userIcon">
                  <img src={user} alt="" height={28} />
                </span>
                <FormattedMessage
                  id={
                    "groupDetails.members" +
                    (group.payload.admins.length === 0 ? ".noAdmins" : "")
                  }
                  values={{
                    count:
                      group.payload.member_count - group.payload.admins.length,
                  }}
                />
              </div>
            </div>
            {isGroupAdmin && group.payload && members.payload && (
              <>
                {invitations.length === 0 ? (
                  <h3 className="groupDetails__membersHeader">
                    <FormattedMessage id="groupDetails.header.members" />
                    <button
                      className="linkButton"
                      onClick={() => {
                        setInvitations([{email: "", is_admin: false}]);
                      }}
                    >
                      <FormattedMessage id="groupDetails.members.invite" />
                    </button>
                  </h3>
                ) : (
                  <>
                    <h3>
                      <FormattedMessage id="groupDetails.header.members" />
                    </h3>
                    <GroupMailInputs
                      invitations={{
                        get: invitations,
                        set: setInvitations,
                      }}
                      adminsOnly={false}
                      disabled={waitingForInvitations}
                    />
                    <div className="groupDetails__membersButtons">
                      <button
                        className="linkButton"
                        disabled={waitingForInvitations}
                        onClick={() => {
                          setInvitations([]);
                        }}
                      >
                        <FormattedMessage id="groupDetails.members.cancel" />
                      </button>
                      <button
                        className="linkButton"
                        disabled={waitingForInvitations}
                        onClick={() => {
                          setWaitingForInvitations(true);
                          API.addGroupMembers(groupId, usableInvitations).then(
                            (response) => {
                              setWaitingForInvitations(false);
                              if (response.success) {
                                addSnackbar({
                                  variant: "timeout",
                                  text: formatMessage(
                                    {
                                      id: "groupDetails.members.success",
                                    },
                                    {invitationCount: usableInvitations.length},
                                  ),
                                  timeout: 3000,
                                });
                                setInvitations([]);
                                API.getGroup(groupId).then((groupResponse) =>
                                  setGroup(groupResponse),
                                );
                                API.getGroupMembers(groupId).then(
                                  (membersResponse) =>
                                    setMembers(membersResponse),
                                );
                              } else
                                addSnackbar({
                                  variant: "close",
                                  text: formatMessage({
                                    id: "groupDetails.members.error",
                                  }),
                                  hasCloseButton: true,
                                });
                            },
                          );
                        }}
                      >
                        <FormattedMessage id="groupDetails.members.send" />
                      </button>
                    </div>
                  </>
                )}
                {members.payload.length === 0 ? (
                  <i className="groupDetails__noItems">
                    <FormattedMessage id="groupDetails.paragraph.noMembers" />
                  </i>
                ) : (
                  <GroupMemberList
                    group={{
                      get: {...group.payload, groupId},
                      set: (
                        updatePayload: (
                          group: GroupDetailResponse,
                        ) => GroupDetailResponse,
                      ) => {
                        setGroup((old) =>
                          !old.payload
                            ? old
                            : {...old, payload: updatePayload(old.payload)},
                        );
                      },
                    }}
                    members={{
                      get: members.payload,
                      set: (
                        updatePayload: (
                          members: GroupMemberResponse[],
                        ) => GroupMemberResponse[],
                      ) => {
                        setMembers((old) =>
                          !old.payload
                            ? old
                            : {...old, payload: updatePayload(old.payload)},
                        );
                      },
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GroupDetails;
